import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Inbox, LayoutDashboard, MessageSquare, Receipt, Wallet } from 'lucide-react';
import { usePageHeader } from '@/components/PageHeader';
import { buttonVariants } from '@/components/ui/button';
import { financeQueueQuery } from '@/lib/finance';
import { usePermissions } from '@/lib/me';

export const Route = createFileRoute('/_app/')({
  component: Dashboard,
});

interface ComingCard {
  title: string;
  description: string;
  phase: string;
  icon: typeof LayoutDashboard;
}

const CARDS: readonly ComingCard[] = [
  {
    title: 'Calendar',
    description: 'Apartments × dates grid with bulk-edit drawer.',
    phase: 'Phase 1c',
    icon: Calendar,
  },
  {
    title: 'Bookings',
    description: 'Bookings list, holds, lifecycle state machine.',
    phase: 'Phase 1e',
    icon: LayoutDashboard,
  },
  {
    title: 'Inbox',
    description: 'Guest messaging — threads, replies, send via the channel manager.',
    phase: 'Phase 1h',
    icon: Inbox,
  },
  {
    title: 'Templates',
    description: 'Automation templates with variables and previews.',
    phase: 'Phase 1i',
    icon: MessageSquare,
  },
];

export function Dashboard() {
  const { can } = usePermissions();
  const canSubmit = can('finance.submit');
  const canConfirm = can('finance.confirm');
  // Only fetched for finance.confirm — a finance_submitter (submit, no
  // confirm) must never issue a request the queue endpoint would 403 for
  // want of finance.read (rulings.md #32's actor exists without it).
  const queue = useQuery({ ...financeQueueQuery({ limit: 1, offset: 0 }), enabled: canConfirm });

  // Title lives in the Topbar (navTitle); no list/count to publish, so the
  // Topbar falls back to the static navSubtitle ("Today's overview").
  usePageHeader({});

  return (
    <>
      {(canSubmit || canConfirm) && (
        <ul className="mb-4 grid gap-4 sm:grid-cols-2">
          {canSubmit && (
            <li className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-foreground">
                <Receipt size={18} />
                <h2 className="text-base font-medium">Submit an expense</h2>
              </div>
              <p className="mb-3 text-sm text-muted">
                Log a payment you made in the field, with a photo of the receipt.
              </p>
              <Link to="/finance/submit" className={buttonVariants({ variant: 'primary', size: 'sm' })}>
                Submit
              </Link>
            </li>
          )}
          {canConfirm && (
            <li className="rounded-xl border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-foreground">
                <Wallet size={18} />
                <h2 className="text-base font-medium">Payments awaiting</h2>
              </div>
              <p className="mb-3 text-sm text-muted">
                {queue.data ? (
                  <span className="text-2xl font-semibold tabular-nums text-foreground">{queue.data.total}</span>
                ) : queue.isLoading ? (
                  'Loading…'
                ) : (
                  '—'
                )}{' '}
                in the confirmation queue.
              </p>
              <Link to="/finance/queue" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                Open queue
              </Link>
            </li>
          )}
        </ul>
      )}
      <ul className="grid gap-4 sm:grid-cols-2">
        {CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <li
              key={card.title}
              className="rounded-xl border border-border bg-card p-5"
            >
              <div className="mb-3 flex items-center gap-2 text-foreground">
                <Icon size={18} />
                <h2 className="text-base font-medium">{card.title}</h2>
              </div>
              <p className="mb-3 text-sm text-muted">{card.description}</p>
              <p className="text-xs uppercase tracking-wider text-muted">
                {card.phase}
              </p>
            </li>
          );
        })}
      </ul>
    </>
  );
}
